import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { IconX } from './icons'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  copied: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null, copied: false }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack)
    this.setState({ errorInfo: info })
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, copied: false })
  }

  private getFullTrace = (): string => {
    const parts: string[] = []
    if (this.state.error) {
      parts.push(`Error: ${this.state.error.message}`)
      if (this.state.error.stack) {
        parts.push(`\nStack:\n${this.state.error.stack}`)
      }
    }
    if (this.state.errorInfo?.componentStack) {
      parts.push(`\nComponent Stack:${this.state.errorInfo.componentStack}`)
    }
    return parts.join('\n')
  }

  private handleCopyTrace = () => {
    const trace = this.getFullTrace()
    void navigator.clipboard.writeText(trace).then(() => {
      this.setState({ copied: true })
      setTimeout(() => this.setState({ copied: false }), 2000)
    })
  }

  override render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <div className="flex flex-col items-center justify-center h-dvh p-6 text-center bg-tg-bg text-tg-text animate-fade-in">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-tg-destructive/10">
          <IconX className="h-8 w-8 text-tg-destructive" />
        </div>
        <h1 className="text-xl font-semibold mb-2">
          Something went wrong
        </h1>
        <p className="text-sm text-tg-hint mb-4 max-w-xs">
          An unexpected error occurred. Please try again.
        </p>
        {this.state.error && (
          <pre className="text-xs text-tg-hint bg-tg-secondary rounded-lg p-3 mb-4 max-w-full max-h-[30vh] overflow-auto whitespace-pre-wrap break-words text-left">
            {this.state.error.message}
            {this.state.error.stack && (
              <>
                {'\n\n'}
                {this.state.error.stack}
              </>
            )}
            {this.state.errorInfo?.componentStack && (
              <>
                {'\n\nComponent Stack:'}
                {this.state.errorInfo.componentStack}
              </>
            )}
          </pre>
        )}
        <div className="flex gap-3">
          <button
            onClick={this.handleCopyTrace}
            className="rounded-lg px-4 py-2 text-sm transition-all duration-150 active:scale-95 bg-tg-secondary text-tg-text hover:bg-tg-bg"
          >
            {this.state.copied ? 'Copied!' : 'Copy Error'}
          </button>
          <button
            onClick={this.handleReset}
            className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-150 active:scale-95 bg-tg-button text-tg-button-text"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }
}
